// src/components/Popups/AppearanceSettingsPopup.jsx
//
// Fereastra „Setări” (butonul „Setări” din josul meniului), pentru toate
// rolurile: tema și ieșirea din cont; elevii au în plus culoarea de bază,
// mărimea textului și animațiile; adminul, lecțiile pe poligon și sectoarele. Aspectul se aplică pe loc și rămâne salvat
// pe dispozitiv (utils/appearanceSettings). Grupurile sunt ListUI glass (rândurile de sticlă, ca
// în profilul elevului): titlu scurt, o descriere foarte scurtă și comutatorul (ToggleUI) în rând.

import React, { useContext, useEffect, useState } from "react";
import Cookies from "js-cookie";
import { useLocation, useNavigate } from "react-router-dom";

import { closePopup } from "../Common/popupUIStore";
import { ButtonUI, ChoiceUI, ListUI, ToggleUI } from "../UI";
import logoutIcon from "../../assets/svg/logout.svg";
import PolygonLessonsSetting from "./Settings/PolygonLessonsSetting";
import DistrictsSetting from "./Settings/DistrictsSetting";
import { UserContext } from "../../UserContext";
import { normalizeRole, ROLES } from "../../auth/access";
import { setHomeEditing } from "../SPanel/Home/homeEditStore";

import {
   ACCENTS,
   DEFAULTS,
   TEXT_SIZES,
   getAppearance,
   setAppearance,
   subscribeAppearance,
} from "../../utils/appearanceSettings";

const SIZE_OPTIONS = TEXT_SIZES.map((s) => ({ value: s.key, label: s.label }));

// un rând de setare (ListUI.Item): titlul scurt, dedesubt o descriere foarte scurtă,
// comutatorul / butonul în dreapta; alegerile (culoarea, mărimea) stau sub text
// (joinedTile: ListUI îi pune colțurile de la marginea grupului, ca la ListUI.Item)
function Setting({ title, hint, control, children, ...rest }) {
   return (
      <ListUI.Item title={title} detail={hint} aside={control} {...rest}>
         {children}
      </ListUI.Item>
   );
}
Setting.joinedTile = true;

export default function AppearanceSettingsPopup() {
   const [settings, setSettings] = useState(getAppearance);
   useEffect(() => subscribeAppearance(setSettings), []);

   const { user } = useContext(UserContext) || {};
   // culoarea, textul și animațiile sunt doar pentru elevi (Header: setStudentAppearance)
   const isStudent = normalizeRole(user?.role) === ROLES.USER;
   // programarea elevilor (lecțiile pe poligon, sectoarele): doar adminul
   const isAdmin = normalizeRole(user?.role) === ROLES.ADMIN;

   const navigate = useNavigate();
   const logout = () => {
      Cookies.remove("access_token");
      sessionStorage.clear();
      closePopup();
      navigate("/");
   };

   // elevul: „Editează pagina principală” — închide Setările, duce pe pagina
   // principală și pornește editarea widget-urilor (homeEditStore)
   const { pathname } = useLocation();
   const editHome = () => {
      closePopup();
      if (pathname !== "/student") navigate("/student");
      setHomeEditing(true);
   };

   const isDefault =
      settings.accent === DEFAULTS.accent &&
      settings.textSize === DEFAULTS.textSize &&
      settings.motion === DEFAULTS.motion;

   const toggle = (key, on, off) => (next) => setAppearance({ [key]: next ? on : off });

   return (
      <div className="appearancePopup">
         <div className="appearancePopup__header">
            <h3 className="appearancePopup__title">Setări</h3>
            <p className="appearancePopup__lead">Se salvează pe acest dispozitiv.</p>
         </div>

         <div className="appearancePopup__content appearancePopup__groups">
            {isStudent && (
               <ListUI glass>
                  <Setting
                     title="Pagina principală"
                     hint="Mută sau adaugă widget-uri."
                     control={
                        <ButtonUI icon="edit" onClick={editHome}>
                           Editează
                        </ButtonUI>
                     }
                  />
               </ListUI>
            )}

            <ListUI glass>
               <Setting
                  title="Tema întunecată"
                  hint="Fundal închis."
                  control={
                     <ToggleUI
                        aria-label="Tema întunecată"
                        checked={settings.theme === "dark"}
                        onChange={toggle("theme", "dark", "light")}
                     />
                  }
               />
               {isStudent && (
                  <Setting title="Culoarea de bază" hint="Butoanele și evidențierile.">
                     <ChoiceUI
                        look="swatches"
                        columns={ACCENTS.length}
                        aria-label="Culoarea de bază"
                        options={ACCENTS.map((a) => ({
                           value: a.key,
                           label: a.label,
                           sublabel: a.label,
                           color: settings.theme === "light" ? a.light : a.dark,
                        }))}
                        value={settings.accent}
                        onChange={(accent) => setAppearance({ accent })}
                     />
                  </Setting>
               )}
               {isStudent && (
                  <Setting title="Mărimea textului" hint="Pe toată platforma.">
                     <ChoiceUI
                        aria-label="Mărimea textului"
                        options={SIZE_OPTIONS}
                        value={settings.textSize}
                        onChange={(textSize) => setAppearance({ textSize })}
                     />
                  </Setting>
               )}
            </ListUI>

            {/* blur-ul și animațiile se opresc pe un dispozitiv mai slab */}
            <ListUI glass>
               <Setting
                  title="Blur în sistem"
                  hint="Sticla meniului și a butoanelor."
                  control={
                     <ToggleUI
                        aria-label="Blur în sistem"
                        checked={settings.blurUi === "on"}
                        onChange={toggle("blurUi", "on", "off")}
                     />
                  }
               />
               <Setting
                  title="Blur progresiv"
                  hint="Sub rândul de sus și la popup-uri."
                  control={
                     <ToggleUI
                        aria-label="Blur progresiv"
                        checked={settings.blurFade === "on"}
                        onChange={toggle("blurFade", "on", "off")}
                     />
                  }
               />
               {isStudent && (
                  <Setting
                     title="Animații"
                     hint="Mișcările din pagini."
                     control={
                        <ToggleUI
                           aria-label="Animații în pagini"
                           checked={settings.motion === "full"}
                           onChange={toggle("motion", "full", "reduced")}
                        />
                     }
                  />
               )}
            </ListUI>

            {isAdmin && (
               <ListUI glass className="appearancePopup__list--admin">
                  <Setting title="Lecții pe poligon" hint="Câte lecții practice. Doar pentru elevii noi.">
                     <PolygonLessonsSetting />
                  </Setting>
                  <Setting title="Sectoare și poligoane" hint="Unde se programează elevii.">
                     <DistrictsSetting />
                  </Setting>
               </ListUI>
            )}

            <div className="appearancePopup__actions">
               {isStudent && (
                  <ButtonUI
                     block
                     disabled={isDefault}
                     onClick={() =>
                        setAppearance({
                           accent: DEFAULTS.accent,
                           textSize: DEFAULTS.textSize,
                           motion: DEFAULTS.motion,
                        })
                     }
                  >
                     Revino la setările inițiale
                  </ButtonUI>
               )}
               <ButtonUI variant="danger" icon={logoutIcon} block onClick={logout}>
                  Ieși din cont
               </ButtonUI>
            </div>
         </div>
      </div>
   );
}
